import type { UserCreateValues, UserUpdateValues } from '@/features/user/user-form-drawer/schema'
import type { IUser, UserCreateRequest, UserUpdateRequest } from '@/shared/api/types/user-types'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCreateUserMutation, useUpdateUserMutation } from '@/entities/user'
import { userCreateSchema, userUpdateSchema } from '@/features/user/user-form-drawer/schema'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/shared/ui/sheet'

interface UserFormDrawerProps {
  open: boolean
  user?: IUser | null
  onClose: () => void
  onSuccess?: () => void
}

const EMPTY_CREATE_VALUES: UserCreateValues = {
  username: '',
  password: '',
  fullName: '',
  email: '',
  phone: '',
  avatarUrl: '',
}

export function UserFormDrawer({ open, user, onClose, onSuccess }: UserFormDrawerProps) {
  const { t } = useTranslation()
  const isEditMode = Boolean(user)

  const createMutation = useCreateUserMutation()
  const updateMutation = useUpdateUserMutation()
  const isPending = createMutation.isPending || updateMutation.isPending

  const createForm = useForm<UserCreateValues>({
    resolver: zodResolver(userCreateSchema),
    mode: 'onChange',
    defaultValues: EMPTY_CREATE_VALUES,
  })

  const updateForm = useForm<UserUpdateValues>({
    resolver: zodResolver(userUpdateSchema),
    mode: 'onChange',
    defaultValues: {
      username: '',
      fullName: '',
      email: '',
      phone: '',
      avatarUrl: '',
    },
  })

  useEffect(() => {
    if (!open) return
    if (user) {
      updateForm.reset({
        username: user.username,
        fullName: user.fullName ?? '',
        email: user.email ?? '',
        phone: user.phone ?? '',
        avatarUrl: user.avatarUrl ?? '',
      })
    } else {
      createForm.reset(EMPTY_CREATE_VALUES)
    }
  }, [open, user, createForm, updateForm])

  const getErrorText = (message: string | undefined) =>
    message ? t(message, { defaultValue: message }) : undefined

  async function onCreateSubmit(values: UserCreateValues) {
    try {
      const payload: UserCreateRequest = {
        username: values.username,
        password: values.password,
        ...(values.fullName ? { fullName: values.fullName } : {}),
        ...(values.email ? { email: values.email } : {}),
        ...(values.phone ? { phone: values.phone } : {}),
        ...(values.avatarUrl ? { avatarUrl: values.avatarUrl } : {}),
      }
      await createMutation.mutateAsync(payload)
      showToast.success(t('user.createSuccess'))
      onClose()
      onSuccess?.()
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  async function onUpdateSubmit(values: UserUpdateValues) {
    if (!user) return
    try {
      const payload: UserUpdateRequest = {
        fullName: values.fullName ?? null,
        email: values.email ?? null,
        phone: values.phone ?? null,
        avatarUrl: values.avatarUrl ?? null,
      }
      await updateMutation.mutateAsync({ id: user.id, payload })
      showToast.success(t('user.updateSuccess'))
      onClose()
      onSuccess?.()
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  const drawerTitle = isEditMode ? t('user.edit') : t('user.create')
  const submitLabel = isEditMode ? t('common.update') : t('common.create')
  const submitLoadingLabel = isEditMode ? t('common.saving') : t('common.creating')

  return (
    <Sheet
      open={open}
      onOpenChange={(open_) => {
        if (!open_) onClose()
      }}
    >
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle>{drawerTitle}</SheetTitle>
        </SheetHeader>

        {isEditMode ? (
          <form
            id="user-form"
            onSubmit={updateForm.handleSubmit(onUpdateSubmit)}
            autoComplete="off"
            className="flex-1 space-y-4 py-4"
          >
            <FormField label={t('user.username')}>
              <Input disabled value={user?.username ?? ''} />
            </FormField>

            <FormField
              label={t('user.fullName')}
              error={getErrorText(updateForm.formState.errors.fullName?.message)}
            >
              <Input autoComplete="off" {...updateForm.register('fullName')} />
            </FormField>

            <FormField
              label={t('user.email')}
              error={getErrorText(updateForm.formState.errors.email?.message)}
            >
              <Input inputMode="email" autoComplete="email" {...updateForm.register('email')} />
            </FormField>

            <FormField
              label={t('user.phone')}
              error={getErrorText(updateForm.formState.errors.phone?.message)}
            >
              <Input type="tel" {...updateForm.register('phone')} />
            </FormField>

            <FormField
              label={t('user.avatarUrl')}
              error={getErrorText(updateForm.formState.errors.avatarUrl?.message)}
            >
              <Input {...updateForm.register('avatarUrl')} />
            </FormField>
          </form>
        ) : (
          <form
            id="user-form"
            onSubmit={createForm.handleSubmit(onCreateSubmit)}
            autoComplete="off"
            className="flex-1 space-y-4 py-4"
          >
            <FormField
              label={t('user.username')}
              required
              error={getErrorText(createForm.formState.errors.username?.message)}
            >
              <Input autoComplete="off" {...createForm.register('username')} />
            </FormField>

            <FormField
              label={t('user.password')}
              required
              error={getErrorText(createForm.formState.errors.password?.message)}
            >
              <Input
                type="password"
                autoComplete="new-password"
                {...createForm.register('password')}
              />
            </FormField>

            <FormField
              label={t('user.fullName')}
              error={getErrorText(createForm.formState.errors.fullName?.message)}
            >
              <Input autoComplete="off" {...createForm.register('fullName')} />
            </FormField>

            <FormField
              label={t('user.email')}
              error={getErrorText(createForm.formState.errors.email?.message)}
            >
              <Input inputMode="email" autoComplete="email" {...createForm.register('email')} />
            </FormField>

            <FormField
              label={t('user.phone')}
              error={getErrorText(createForm.formState.errors.phone?.message)}
            >
              <Input type="tel" {...createForm.register('phone')} />
            </FormField>

            <FormField
              label={t('user.avatarUrl')}
              error={getErrorText(createForm.formState.errors.avatarUrl?.message)}
            >
              <Input {...createForm.register('avatarUrl')} />
            </FormField>
          </form>
        )}

        <SheetFooter className="pt-4 border-t border-zinc-100">
          <Button variant="outline" onClick={onClose} type="button">
            {t('common.cancel')}
          </Button>
          <Button form="user-form" type="submit" disabled={isPending} aria-busy={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending ? submitLoadingLabel : submitLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
